import { useEffect, useMemo, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Plus, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { fetchJson, type Cycle, type Settings } from '@/lib/api';
import { formatDateTime } from '@/lib/utils';
import { useConfirm } from '@/components/ConfirmDialog';

const CADENCE_LABELS: Record<string, string> = {
  manual: 'ידני',
  daily: 'יומי',
  weekly: 'שבועי',
  monthly: 'חודשי',
  unlimited: 'ללא הגבלה'
};

export default function Cycles() {
  const confirm = useConfirm();
  const [items, setItems] = useState<Cycle[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed'>('all');
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<Settings | null>(null);
  const [creating, setCreating] = useState(false);
  const [newCycleName, setNewCycleName] = useState('');

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const url = `/api/cycles?page=${page}&pageSize=200${statusFilter !== 'all' ? `&status=${statusFilter}` : ''}`;
      const res = await fetchJson<{ items: Cycle[]; total: number }>(url);
      setItems(res.items);
      setTotal(res.total);
      const s = await fetchJson<Settings>('/api/settings');
      setSettings(s);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isManual = settings?.cadence === 'manual';
  const isUnlimited = settings?.cadence === 'unlimited';

  const columns: DataTableColumn<Cycle>[] = useMemo(
    () => [
      { key: 'name', header: 'שם', cell: (c) => c.name || '-', exportValue: (c) => c.name || '' },
      {
        key: 'status',
        header: 'סטטוס',
        cell: (c) => (
          <Badge variant={c.status === 'open' ? 'success' : 'muted'}>
            {c.status === 'open' ? 'פתוח' : 'סגור'}
          </Badge>
        ),
        exportValue: (c) => (c.status === 'open' ? 'פתוח' : 'סגור')
      },
      {
        key: 'startsAt',
        header: 'תחילה',
        cell: (c) => <span className="text-sm">{formatDateTime(c.startsAt)}</span>,
        exportValue: (c) => formatDateTime(c.startsAt)
      },
      {
        key: 'closedAt',
        header: 'סיום',
        cell: (c) => <span className="text-sm">{c.closedAt ? formatDateTime(c.closedAt) : '-'}</span>,
        exportValue: (c) => (c.closedAt ? formatDateTime(c.closedAt) : '')
      },
      {
        key: 'createdBy',
        header: 'נוצר ע"י',
        cell: (c) => (
          <span className="text-xs text-muted-foreground">
            {c.createdBy === 'auto' ? 'אוטומטי' : c.createdBy}
          </span>
        ),
        exportValue: (c) => (c.createdBy === 'auto' ? 'אוטומטי' : c.createdBy)
      },
      {
        key: 'sessionsCount',
        header: 'רשומות',
        cell: (c) => c.sessionsCount ?? 0,
        exportValue: (c) => c.sessionsCount ?? 0
      },
      {
        key: 'actions',
        header: 'פעולות',
        align: 'end',
        exportable: false,
        stopClickPropagation: true,
        cell: (c) =>
          isManual && c.status === 'open' ? (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const ok = await confirm({
                  title: 'לסגור את המחזור?',
                  description: 'לאחר הסגירה, לקוחות לא יוכלו עוד להוסיף רשומות למחזור הזה.',
                  confirmText: 'סגור מחזור'
                });
                if (!ok) return;
                await fetchJson(`/api/cycles/${c.id}/close`, { method: 'PATCH' });
                toast.success('המחזור נסגר');
                refresh();
              }}
            >
              <X className="h-4 w-4" />
              סגור
            </Button>
          ) : null
      }
    ],
    [isManual, confirm, refresh]
  );

  return (
    <div className="page-fill flex flex-col gap-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-heading text-2xl font-semibold tracking-tight">מחזורים</h1>
            {settings && (
              <Badge variant="outline">
                {CADENCE_LABELS[settings.cadence] || settings.cadence}
              </Badge>
            )}
          </div>
          <p className="text-sm text-muted-foreground">{total} מחזורים בסך הכל</p>
        </div>
        {isManual && (
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />
            פתח מחזור חדש
          </Button>
        )}
      </div>

      {isUnlimited && (
        <Card>
          <CardContent className="py-6 text-center text-sm text-muted-foreground">
            במצב "ללא הגבלה" אין מחזורים. כל שיחה יוצרת רשומה חדשה.
          </CardContent>
        </Card>
      )}

      {!isUnlimited && (
        <Card className="flex min-h-0 flex-1 flex-col">
          <CardHeader className="flex-none flex-row items-center justify-between space-y-0 pb-3">
            <Select
              value={statusFilter}
              onValueChange={(v: 'all' | 'open' | 'closed') => {
                setPage(1);
                setStatusFilter(v);
              }}
            >
              <SelectTrigger className="w-[12rem]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">כל המצבים</SelectItem>
                <SelectItem value="open">פתוחים</SelectItem>
                <SelectItem value="closed">סגורים</SelectItem>
              </SelectContent>
            </Select>
            {!isManual && (
              <p className="text-xs text-muted-foreground">המחזורים מנוהלים אוטומטית</p>
            )}
          </CardHeader>
          <CardContent className="flex min-h-0 flex-1 flex-col">
            <DataTable
              columns={columns}
              data={items}
              getRowKey={(c) => c.id}
              loading={loading}
              emptyMessage="אין מחזורים להצגה."
              exportFileName="מחזורים"
              exportSheetName="מחזורים"
              pagination
            />
          </CardContent>
        </Card>
      )}

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>פתיחת מחזור חדש</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="cycle_name">שם (אופציונלי)</Label>
              <Input
                id="cycle_name"
                value={newCycleName}
                onChange={(e) => setNewCycleName(e.target.value)}
                placeholder="למשל: סבב מאי 2026"
              />
              <p className="text-xs text-muted-foreground">
                פתיחה תסגור אוטומטית את המחזור הקודם אם הוא פתוח.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreating(false)}>
                ביטול
              </Button>
              <Button
                onClick={async () => {
                  await fetchJson('/api/cycles', {
                    method: 'POST',
                    body: JSON.stringify({ name: newCycleName.trim() || null })
                  });
                  toast.success('המחזור נפתח');
                  setCreating(false);
                  setNewCycleName('');
                  refresh();
                }}
              >
                פתח מחזור
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
